import React, { useState, useRef, useCallback } from 'react';
import { Send } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ChatInputProps {
  onSend: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  disabled = false,
  placeholder = 'Ask IntelliHealth a question...',
}) => {
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || disabled) return;
    onSend(text);
    setInput('');
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [input, disabled, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    // Auto-resize
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  const canSend = input.trim().length > 0 && !disabled;

  return (
    <div className="flex items-end gap-2">
      <textarea
        ref={textareaRef}
        value={input}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={disabled ? 'Waiting...' : placeholder}
        disabled={disabled}
        rows={1}
        className={cn(
          'flex-1 resize-none rounded-lg border border-slate-200',
          'px-3 py-2 text-sm text-slate-700',
          'placeholder:text-slate-400',
          'focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal',
          'transition-colors',
          disabled && 'bg-slate-50 cursor-not-allowed',
        )}
      />
      <button
        onClick={handleSend}
        disabled={!canSend}
        className={cn(
          'p-2 rounded-lg transition-colors flex-shrink-0',
          canSend
            ? 'bg-brand-teal text-white hover:bg-brand-teal/90'
            : 'bg-slate-100 text-slate-400 cursor-not-allowed',
        )}
      >
        <Send className="w-4 h-4" />
      </button>
    </div>
  );
};

export default ChatInput;
